import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { deleteRequest, getRequest } from "../../../components/axiosClient";
import {
  errorNotification,
  successNotification,
} from "../../../components/toast-notification/common-toast";
import {
  ADD_PROJECT_NOTES,
  DELETE_PROJECT_NOTES,
  GET_PROJECT_NOTES,
  UPDATE_PROJECT_NOTES,
} from "../../../components/utils";
import Modal from "../Modal/Modal";
import NewModal from "../Modal/NewModal";

import noDataFound from "../../../components/assets/No data-rafiki 1.svg";
import TrashIcon from "../../../components/common/TrashIcon";

export const Note = ({ projectId }) => {
  const [listShow, setListShow] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const role_accesses =
    JSON.parse(secureLocalStorage.getItem("role_accesses")).find(
      (access) => access.module.name === "Projects"
    )?.access_type !== "Read";

  const navigate = useNavigate();

  const getNotes = async () => {
    try {
      const res = await getRequest(
        `${GET_PROJECT_NOTES}?project_id=${projectId}`,
        null,
        navigate
      );

      if (res.type === 2) {
        errorNotification(res.errormessage);
        navigate("/404");
      } else {
        setNotes(res.data.data);
      }
    } catch (error) {
      errorNotification(error);
    }
  };

  useEffect(() => {
    getNotes();
  }, [projectId]);

  const handleAdd = () => {
    setIsEditing(false);
    setEditingNoteId(null);
    reset();
    setListShow(true);
  };

  const handleEdit = (note) => {
    setIsEditing(true);
    setEditingNoteId(note.id);
    setValue("title", note.title);
    setValue("description", note.description);
    setListShow(true);
  };

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        const payload = {
          title: data.title,
          description: data.description,
          project_note_id: editingNoteId,
        };

        const config = {
          method: "patch",
          url: `${UPDATE_PROJECT_NOTES}`,
          headers: {
            Authorization: secureLocalStorage.getItem("token"),
          },
          data: payload,
        };

        const res = await axios(config);
        if (res.data.success) {
          successNotification(res.data.message);
          getNotes();
          reset();
          setListShow(false);
          setIsEditing(false);
          setEditingNoteId(null);
        }
      } else {
        const payload = {
          project_id: projectId,
          title: data.title,
          description: data.description,
        };

        const config = {
          method: "post",
          url: `${ADD_PROJECT_NOTES}`,
          headers: {
            Authorization: secureLocalStorage.getItem("token"),
          },
          data: payload,
        };

        const res = await axios(config);
        if (res.data.success) {
          successNotification(res.data.message);
          getNotes();
          reset();
          setListShow(false);
        }
      }
    } catch (error) {
      errorNotification(
        error.response?.data?.message ||
        `An error occurred while ${isEditing ? "updating" : "adding"
        } the note.`
      );
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteRequest(
        `${DELETE_PROJECT_NOTES}?project_note_id=${noteToDelete}`,
        null,
        navigate
      );
      if (res.type === 1) {
        successNotification(res.response.data.message);
        getNotes();
        setShowModal(false);
        setNoteToDelete(null);
      } else if (res.type === 2) {
        errorNotification(res.errormessage || res.errors);
      }
    } catch (error) {
      console.error("Error removing note: ", error);
    }
  };

  const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    setShowModal(true);
  };

  return (
    <div className="hunter_audit_dashboard h-full flex flex-col">
      <div>
        {role_accesses && (
          <button className="client__save__btn mx-3 mb-3" onClick={handleAdd}>
            + Add Note
          </button>
        )}
        {notes?.length === 0 ? (
          <div className="no__transcation">
            <img src={noDataFound} alt="No data found" loading="lazy" />
          </div>
        ) : (
          <div className="min-h-[600px]">
            <table className="table">
              <thead>
                <tr>
                  <th className="!border-b-[#d3d3d3] font-medium">Sr no </th>
                  <th className="!border-b-[#d3d3d3] font-medium">Title </th>
                  <th className="!border-b-[#d3d3d3] font-medium">
                    Description
                  </th>
                  <th className="!border-b-[#d3d3d3] font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {notes?.map((note, index) => (
                  <OverlayTrigger
                    placement="auto"
                    overlay={
                      <div
                        id={`tooltip-auto`}
                        style={{
                          position: "absolute",
                          margin: "5px",
                          backgroundColor: "#f4f4f4",
                          padding: "3px 10px",
                          color: "#000000",
                          fontSize: "12px",
                          borderRadius: 3,
                        }}
                      >
                        {`${note.user.first_name} ${note.user.last_name} created at ${dayjs(
                          note.createdAt
                        ).format("hh:mm DD-MMM-YYYY")}`}
                      </div>
                    }
                    key={note.id}
                  >
                    <tr>
                      <td className="table_tr_style align-middle w-[66px]">
                        <div className="truncate">{index + 1}</div>
                      </td>
                      <td className="table_tr_style align-middle !max-w-[20vw]">
                        <div className="truncate">
                          {note?.title ? note?.title : "Note title"}
                        </div>
                      </td>
                      <td className="table_tr_style align-middle">
                        {note.description}
                      </td>
                      {role_accesses && (
                        <td className="text-right">
                          <div className="flex justify-content-end">
                            <button
                              className="text-right h-[22px] mx-2"
                              onClick={() => handleEdit(note)}
                            >
                              <EditOutlinedIcon sx={{ color: "#4594f1" }} />
                            </button>
                            <button
                              className="text-right h-[22px] mx-2 cursor-pointer"
                              onClick={() => handleDeleteClick(note.id)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  </OverlayTrigger>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        className="client-modal"
        title={isEditing ? "Edit Note" : "Add Note"}
        onClose={() => {
          setListShow(false);
          setIsEditing(false);
          setEditingNoteId(null);
          reset();
        }}
        show={listShow}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="modal-body add__client__form"
        >
          <div>
            <h6 className="modal-heading">Title</h6>
            <input
              placeholder="Note title"
              className="client__name text-capitalize"
              type="text"
              autoComplete="off"
              {...register("title", {
                required: "Title is required",
                minLength: {
                  value: 3,
                  message: "Title must be at least 3 characters",
                },
              })}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">
                {errors.title.message}
              </p>
            )}
          </div>
          <div className="mt-3">
            <h6 className="modal-heading">Description</h6>
            <textarea
              placeholder="Note description"
              className="border rounded-xl p-2 text-capitalize w-full h-[150px] resize-none"
              rows="4"
              autoComplete="off"
              {...register("description", {
                required: "Description is required",
              })}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="add__close__section mt-4">
            <button type="submit" className="client__save__btn w-100">
              {isEditing ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </Modal>

      <NewModal
        title="Are you Sure?"
        onClose={() => setShowModal(false)}
        show={showModal}
      >
        <div className="modal-body add__cmp__form">
          <h6>Do you really want to delete this note?</h6>
          <div className="add__close__section">
            <button
              type="button"
              className="bg-[#F5F5F5] text-[#5D5D5D] w-[fit-content] px-4 py-2 rounded-[12px] me-3"
              onClick={() => setShowModal(false)}
            >
              No
            </button>
            <button
              type="button"
              className="bg-[#267072] text-[#fff] border-[1px] flex items-center w-[fit-content] px-3 py-2 rounded-[12px] whitespace-nowrap"
              onClick={confirmDelete}
            >
              Yes
            </button>
          </div>
        </div>
      </NewModal>
    </div>
  );
};
